import '@sendbird/uikit-react/dist/index.css'

import CircularProgress from '@material-ui/core/CircularProgress'
import DescriptionIcon from '@material-ui/icons/Description'
import { GroupChannel } from '@sendbird/uikit-react/GroupChannel'
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider'
import MessageContent from '@sendbird/uikit-react/ui/MessageContent'
import axios from 'axios'
import * as HttpStatus from 'http-status-codes'
import { useEffect, useState } from 'react'
import { Show, useNotify } from 'react-admin'
import { Link } from 'react-router-dom'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { useUserProvider } from 'shared/components/Context/UserContext'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { lightGray1, primaryBlack, royalBlue } from 'shared/theme/colors'

import { getAccessToken, getInviteToSendbirdIssueChat } from 'api/sendbird'
import { getSendbirdApplicationId } from 'config/config'
import { TERMINAL_ISSUE_STATUSES } from 'helpers/constants'
import { captureExceptionIfProduction, getFormattedDate } from 'helpers/utils'
import { useHasSendbirdChat } from 'hooks/useHasFeatureFlag'
import { getLoggedInUserIdentityWithCache } from '../../api/identity'
import { IssueStatusDropdown } from './IssueSectionV2'

// a row with two columns for label and value
// optionally, pass in a 'to' prop to make the value a link
const LabeledData = ({ label, value, to }: { label: string; value: string; to?: string }) => (
  <FlexBox mb={1}>
    <FlexBox isColumn width="150px">
      <Text variant="header3" fontColor={lightGray1}>
        {label}
      </Text>
    </FlexBox>
    <FlexBox isColumn width="300px">
      {to ? (
        <Link
          to={to}
          style={{
            cursor: 'pointer',
            textDecoration: 'none',
          }}
        >
          <Text variant="label2" color={royalBlue} fontWeight={600}>
            {value}
          </Text>
        </Link>
      ) : (
        <Text variant="label2" fontWeight={600}>
          {value}
        </Text>
      )}
    </FlexBox>
  </FlexBox>
)

const InternalNote = ({
  issueId,
  notes,
  issueClass,
}: {
  issueId: string
  notes: string
  issueClass: string
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [editedNotes, setEditedNotes] = useState(notes || '')
  const [showEdit, setShowEdit] = useState(false)
  const { showBanner } = useUserProvider()

  const updateNotes = async () => {
    setIsLoading(true)

    try {
      const data = {
        issue_class: issueClass,
        issue_id: issueId,
        notes: editedNotes,
      }
      const response = await axios.post('/api/v3/issues/update_notes/', data)
      if (response.status !== HttpStatus.OK) {
        throw new Error('Failed to update notes')
      }
      showBanner('success', { message: 'Internal note updated' })
      setIsLoading(false)
      setShowEdit(false)
      window.location.reload()
    } catch (err: any) {
      const error = err?.response?.data || err?.data || err?.message || err || {}
      showBanner('error', { message: `Something went wrong. ${error}` })
      captureExceptionIfProduction(err)
      setShowEdit(false)
      setIsLoading(false)
    }
  }

  return (
    <>
      <LabeledData label="Internal Note" value={notes || '-'} />
      <Box ml="150px" mb={2}>
        <Button variant="text" onClick={() => setShowEdit(true)}>
          Edit Note
        </Button>
      </Box>
      <SimpleDialog isOpen={showEdit} onClose={() => setShowEdit(false)} size="small" alignText="left">
        <div style={{ width: '100%' }}>
          <Text variant="header2" fontColor={primaryBlack}>
            Edit Internal Note
          </Text>
        </div>
        <Box mt={3} />

        <TextInput
          placeholder="Internal Note"
          value={editedNotes}
          onChange={(e) => setEditedNotes(e.target.value)}
        />

        <Box mt={5} />
        <Button
          type="submit"
          margin="0 auto"
          onClick={updateNotes}
          isDisabled={!editedNotes}
          variant="primary"
          size="large"
          isLoading={isLoading}
        >
          Save
        </Button>
        <Box mt={3} />
        <Button variant="text" margin="0 auto" onClick={() => setShowEdit(false)}>
          Cancel
        </Button>
      </SimpleDialog>
    </>
  )
}

const Driver = ({ record }: { record: any }) => (
  <LabeledData
    label="Driver"
    value={`${record.reported_for?.first_name} ${record.reported_for?.last_name}`}
    to={`/users/${record.reported_for?.id}/show`}
  />
)

const ZendeskTicketLink = ({ ticketId }: { ticketId?: number }) => {
  if (!ticketId) {
    return null
  }
  return (
    <FlexBox mb={1}>
      <FlexBox isColumn width="150px">
        <Text variant="header3" fontColor={lightGray1}>
          Zendesk
        </Text>
      </FlexBox>
      <FlexBox isColumn width="300px">
        <a
          href={`https://cloudtrucks.zendesk.com/agent/tickets/${ticketId}`}
          target="_blank"
          rel="noreferrer"
          style={{
            cursor: 'pointer',
            textDecoration: 'none',
          }}
        >
          <Text variant="label2" color={royalBlue} fontWeight={600}>
            {ticketId}
          </Text>
        </a>
      </FlexBox>
    </FlexBox>
  )
}

const CreatedAt = ({ record }: { record: any }) => (
  <LabeledData
    label="Created At"
    value={getFormattedDate(record.date_created, record.stop?.location_timezone)}
  />
)

const CreatedBy = ({ record }: { record: any }) => {
  const fullName = `${record.reported_by?.first_name} ${record.reported_by?.last_name}`.trim()
  const displayName = fullName || record.reported_by?.email || '-'
  return <LabeledData label="Created By" value={displayName} to={`/users/${record.reported_by?.id}/show`} />
}

const UpdatedAt = ({ record }: { record: any }) => (
  <LabeledData label="Updated" value={record.status_updated_at_time_difference + ' ago'} />
)

const Messages = ({ record }: { record: any }) => {
  const [loading, setLoading] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const hasSendbirdChat = useHasSendbirdChat()
  const appId = getSendbirdApplicationId()
  const notify = useNotify()

  useEffect(() => {
    async function fetchData() {
      if (hasSendbirdChat && record.sendbird_channel_url && !accessToken) {
        setLoading(true)
        try {
          const { user } = await getLoggedInUserIdentityWithCache()
          if (!user) {
            throw new Error('Failed to get logged in user')
          }

          const inviteSuccess = await getInviteToSendbirdIssueChat(record.id, record.issue_class)
          if (!inviteSuccess) {
            throw new Error('Failed to join Sendbird chat')
          }

          const token = await getAccessToken(user.id)
          if (!token) {
            throw new Error('Failed to get Sendbird access token')
          }

          setUserId(user.id)
          setAccessToken(token)
        } catch (err) {
          const error = err as Error
          notify(`Error: ${error.message}`, { type: 'error' })
          captureExceptionIfProduction(error)
        } finally {
          setLoading(false)
        }
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, hasSendbirdChat])

  if (!hasSendbirdChat) {
    return null
  }

  if (!record.sendbird_channel_url) {
    return (
      <Box width="600px">
        <Text variant="header3">In-App Chat is Disabled</Text>
        <Box mt={2}>
          <Text variant="paragraph1">This is either because:</Text>
          <Box mt={2}>
            <Text variant="paragraph1">
              (1) The driver has not yet been granted access to in-app chat, or
            </Text>
            <Text variant="paragraph1">(2) This issue was created before the driver was granted access.</Text>
          </Box>
          <Box mt={2}>
            <Text variant="paragraph1">Please communicate with the driver with legacy comms.</Text>
          </Box>
        </Box>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box mt={3}>
        <CircularProgress color="primary" size={60} />
      </Box>
    )
  }

  if (userId && accessToken) {
    const renderNothing = () => <></>
    const areMessagesDisabled = TERMINAL_ISSUE_STATUSES.includes(record.status)
    // have had inconsistent performance w/ reactions, so disabling
    return (
      <Box width="600px" height="600px">
        <SendbirdProvider appId={appId} userId={userId} accessToken={accessToken}>
          {areMessagesDisabled ? (
            <>
              <Box mb={2}>
                <Text variant="header3">Issue is closed. Sending new messages is disabled.</Text>
              </Box>
              <GroupChannel
                channelUrl={record.sendbird_channel_url}
                renderChannelHeader={renderNothing}
                renderMessageInput={renderNothing}
                isReactionEnabled={false}
                renderMessageContent={(contentProps) => (
                  <MessageContent {...contentProps} renderMessageMenu={() => null} />
                )}
              />
            </>
          ) : (
            <GroupChannel
              channelUrl={record.sendbird_channel_url}
              renderChannelHeader={renderNothing}
              isReactionEnabled={false}
              renderMessageContent={(contentProps) => (
                <MessageContent {...contentProps} renderMessageMenu={() => null} />
              )}
            />
          )}
        </SendbirdProvider>
      </Box>
    )
  }

  return (
    <Box width="600px">
      <Text variant="header3">Trouble Loading Chat</Text>
      <Box mt={2}>
        <Text variant="paragraph1">Please let #tech-support know.</Text>
        <Box mt={2}>
          <Text variant="paragraph1">In the meantime, communicate with the driver via legacy comms.</Text>
        </Box>
      </Box>
    </Box>
  )
}

const Issue = ({ record, overview }: { record: any; overview: any }) => {
  const hasSendbirdChat = useHasSendbirdChat()
  usePageTitle(`${record?.human_type} Issue`)

  return (
    <FlexBox isColumn ml={5} mt={5} mb={5}>
      <FlexBox>
        <FlexBox isColumn width="400px">
          <Text variant="header1">{record.human_type}</Text>
        </FlexBox>
        <FlexBox isColumn>
          <FlexBox gap="4px">
            <IssueStatusDropdown issue={record} />
          </FlexBox>
        </FlexBox>
      </FlexBox>

      <FlexBox>
        <FlexBox isColumn width="550px">
          {overview}
        </FlexBox>
        <FlexBox isColumn mt={2} mb={5}>
          {hasSendbirdChat && <Messages record={record} />}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}

// renders job issue details
const JobIssue = ({ record }: { record: any }) => {
  return (
    <Issue
      record={record}
      overview={
        <>
          <Box mt={3}>
            <Driver record={record} />
            <LabeledData label="Job" value={record.job.human_id} to={`/jobs/${record.job.id}/show`} />
            <CreatedAt record={record} />
            <CreatedBy record={record} />
            <UpdatedAt record={record} />
            <ZendeskTicketLink ticketId={record.zendesk_ticket_id} />
          </Box>

          <Box mt={3}>
            <Text variant="header3">DETAILS</Text>
            <Box mt={1}>
              <LabeledData label="Description" value={record.description} />
              <InternalNote issueId={record.id} issueClass="JOB" notes={record.notes} />
              <LabeledData label="Stop" value={record.stop_label || '-'} />
              <LabeledData
                label="Check-in time"
                value={
                  record.checked_in_at
                    ? getFormattedDate(record.checked_in_at, record.stop?.location_timezone)
                    : '-'
                }
              />
              <LabeledData
                label="Check-out time"
                value={
                  record.checked_out_at
                    ? getFormattedDate(record.checked_out_at, record.stop?.location_timezone)
                    : '-'
                }
              />
              <LabeledData label="Wait time" value={record.wait_time || '-'} />
              <FlexBox mb={1}>
                <FlexBox isColumn width="150px">
                  <Text variant="header3" fontColor={lightGray1}>
                    Document
                  </Text>
                </FlexBox>
                <FlexBox isColumn width="300px">
                  {record.document?.human_type ? (
                    <FlexBox gap="5px">
                      <DescriptionIcon color="primary" />
                      <Text variant="label2" fontWeight={600}>
                        {record.document?.human_type}
                      </Text>
                    </FlexBox>
                  ) : (
                    <Text variant="label2" fontWeight={600}>
                      -
                    </Text>
                  )}
                </FlexBox>
              </FlexBox>
            </Box>
          </Box>
        </>
      }
    />
  )
}

// renders load issue details
const LoadIssue = ({ record }: { record: any }) => {
  return (
    <Issue
      record={record}
      overview={
        <>
          <Box mt={3}>
            <Driver record={record} />
            <LabeledData label="Load" value={record.load.id} to={`/loads/${record.load.id}/show`} />
            <CreatedAt record={record} />
            <CreatedBy record={record} />
            <UpdatedAt record={record} />
            <ZendeskTicketLink ticketId={record.zendesk_ticket_id} />
          </Box>

          <Box mt={3}>
            <Text variant="header3">DETAILS</Text>
            <Box mt={1}>
              <LabeledData label="Description" value={record.description} />
              <InternalNote issueId={record.id} issueClass="LOAD" notes={record.notes} />
            </Box>
          </Box>
        </>
      }
    />
  )
}

// renders generic issue details
const GenericIssue = ({ record }: { record: any }) => {
  return (
    <Issue
      record={record}
      overview={
        <>
          <Box mt={3}>
            <Driver record={record} />
            <CreatedAt record={record} />
            <CreatedBy record={record} />
            <UpdatedAt record={record} />
            <ZendeskTicketLink ticketId={record.zendesk_ticket_id} />
          </Box>

          <Box mt={3}>
            <Text variant="header3">DETAILS</Text>
            <Box mt={1}>
              <LabeledData label="Description" value={record.description} />
              <InternalNote issueId={record.id} issueClass="GENERIC" notes={record.notes} />
            </Box>
          </Box>
        </>
      }
    />
  )
}

export const IssueRouter = ({ record }: { record: any }) => {
  if (record.issue_class === 'JOB') {
    return <JobIssue record={record} />
  } else if (record.issue_class === 'LOAD') {
    return <LoadIssue record={record} />
  } else if (record.issue_class === 'GENERIC') {
    return <GenericIssue record={record} />
  } else {
    throw new Error(`Unknown issue class: ${record.issue_class}`)
  }
}

const IssueShow = (props: any) => {
  return (
    <Show {...props}>
      <IssueRouter {...props} />
    </Show>
  )
}

export default IssueShow
